import { PropsWithChildren, useMemo } from 'react';
import { cn } from '@bridebook/aura';
import { cva } from 'class-variance-authority';

const roundedModalHeaderIllustrationVariants = cva(
  'flex size-[80px] items-center justify-center rounded-full',
  {
    variants: {
      color: {
        'indigo-crush': 'bg-indigo-crush',
        'indigo-crush-04': 'bg-indigo-crush-04',
      },
    },
  },
);

type Props = {
  className?: string;
  variant?: 'confetti' | 'alert';
};
export const RoundedModalHeaderIllustration = ({
  children,
  className,
  variant = 'confetti',
}: PropsWithChildren<Props>) => {
  const content = useMemo(() => {
    switch (variant) {
      case 'confetti':
        return <ConfettiIllustration />;
      case 'alert':
        return <AlertIllustration />;
      default:
        return children;
    }
  }, [variant, children]);

  const color = useMemo(() => {
    switch (variant) {
      case 'confetti':
        return 'indigo-crush';
      case 'alert':
        return 'indigo-crush-04';
      default:
        return 'indigo-crush';
    }
  }, [variant]);

  return (
    <div className={cn(roundedModalHeaderIllustrationVariants({ color }), className)}>
      <>{content}</>
    </div>
  );
};

const ConfettiIllustration = () => (
  <svg width={78} height={78} viewBox="0 0 78 78" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M53.881 45.533L32.9539 24.606L19.7934 56.0768C19.1034 57.7267 20.7603 59.3835 22.4101 58.6936L53.881 45.533Z"
      fill="#FDD25D"
    />
    <ellipse
      cx="43.4175"
      cy="35.0695"
      rx="14.7977"
      ry="3.28828"
      transform="rotate(45 43.4175 35.0695)"
      fill="#F8F89C"
    />
    <mask
      id="mask0_11252_3785"
      style={{ maskType: 'alpha' }}
      maskUnits="userSpaceOnUse"
      x={19}
      y={24}
      width={35}
      height={35}>
      <path
        d="M53.881 45.533L32.9539 24.606L19.7934 56.0768C19.1034 57.7267 20.7603 59.3835 22.4101 58.6936L53.881 45.533Z"
        fill="#C8FD5D"
      />
    </mask>
    <g mask="url(#mask0_11252_3785)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M33.545 45.4702C29.9475 41.8728 26.5447 37.5923 24.9583 34.0534L29.178 32.1619C30.4255 34.9447 33.3695 38.7551 36.8148 42.2004C40.289 45.6745 43.8707 48.3742 46.199 49.2568L44.5597 53.5808C41.2705 52.3339 37.1136 49.0388 33.545 45.4702Z"
        fill="#FF766A"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M26.584 52.2425C24.2108 49.8692 22.069 46.8309 21.3524 44.0295L24.9364 43.1126C25.4187 44.998 27.0456 47.4723 29.1999 49.6266C31.3803 51.8069 33.6879 53.2479 35.2369 53.5209L34.5948 57.1642C31.9099 56.691 28.9312 54.5896 26.584 52.2425Z"
        fill="#FF766A"
      />
    </g>
    <ellipse
      cx="43.7444"
      cy="34.7425"
      rx="12.0231"
      ry="1.96967"
      transform="rotate(45 43.7444 34.7425)"
      fill="#C18120"
    />
    <path
      d="M42.4902 32.113C42.7614 31.9958 42.9985 31.7888 43.1376 31.5199C43.9047 30.0359 43.1315 28.9459 42.567 28.1499C41.692 26.9165 41.9745 26.3726 43.6852 25.9867C46.9159 25.2579 48.188 22.9227 46.4994 20.5424C45.6244 19.3091 45.9068 18.7655 47.6173 18.3797C50.848 17.6511 52.12 15.3159 50.4315 12.9357C49.8846 12.1649 49.7441 11.8673 49.9303 11.5072C50.5811 10.2481 48.5912 9.59645 47.9403 10.8555C47.1733 12.3394 47.9465 13.4293 48.5112 14.2252C49.386 15.4584 49.1038 16.0022 47.3932 16.3879C44.1625 17.1165 42.8905 19.4518 44.5791 21.8319C45.4541 23.0653 45.1715 23.609 43.4609 23.9949C40.2302 24.7236 38.9582 27.059 40.6466 29.4394C41.1934 30.2102 41.3338 30.5079 41.1477 30.8682C40.725 31.6857 41.6007 32.4977 42.4902 32.113Z"
      fill="white"
    />
    <circle cx="38.9998" cy="7.66187" r={2} fill="#9497EE" />
    <path
      d="M46.4085 35.6509C50.1079 32.8376 54.9172 32.044 59.3565 32.4139"
      stroke="#FFB8B1"
      strokeWidth={3}
      strokeLinecap="round"
    />
    <path
      d="M34.8479 11.9819C37.6225 13.8316 37.9924 16.9761 37.6225 20.3056"
      stroke="#FDBD5D"
      strokeWidth={3}
      strokeLinecap="round"
    />
    <path
      d="M59.311 40.5969L61.3244 39.7956C62.4907 39.3314 63.8222 39.7667 64.489 40.8302C65.1559 41.8937 66.4874 42.329 67.6537 41.8648L69.6671 41.0635"
      stroke="#FF766A"
      strokeWidth={3}
      strokeLinecap="round"
    />
    <path
      d="M56.2875 23.0881C55.2877 21.9542 55.5739 20.0679 56.9268 18.8749C58.2797 17.6818 60.187 17.6339 61.1869 18.7677C62.1868 19.9016 61.9006 21.7879 60.5477 22.981C59.1948 24.174 57.2874 24.222 56.2875 23.0881Z"
      fill="#69D0BC"
    />
  </svg>
);

const AlertIllustration = () => (
  <svg width={42} height={37} viewBox="0 0 42 37" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.99599 36.368C1.62557 36.368 1.28883 36.2757 0.985763 36.0912C0.682696 35.9067 0.446978 35.6622 0.278608 35.3578C0.110238 35.0534 0.0179707 34.7247 0.00180711 34.3718C-0.0143564 34.0189 0.0779104 33.6741 0.278608 33.3373L18.9677 1.01022C19.1698 0.673481 19.4311 0.420926 19.7516 0.252555C20.0722 0.0841852 20.4002 0 20.7356 0C21.071 0 21.3997 0.0841852 21.7216 0.252555C22.0435 0.420926 22.3041 0.673481 22.5035 1.01022L41.1926 33.3373C41.3946 33.6741 41.4876 34.0196 41.4714 34.3738C41.4553 34.7281 41.3623 35.056 41.1926 35.3578C41.0229 35.6595 40.7872 35.904 40.4854 36.0912C40.1837 36.2784 39.847 36.3707 39.4752 36.368H1.99599ZM20.7356 30.3067C21.3081 30.3067 21.7883 30.1127 22.1762 29.7248C22.5641 29.3368 22.7574 28.8573 22.7561 28.2862C22.7547 27.7151 22.5607 27.2356 22.1742 26.8477C21.7876 26.4597 21.3081 26.2658 20.7356 26.2658C20.1631 26.2658 19.6836 26.4597 19.297 26.8477C18.9105 27.2356 18.7165 27.7151 18.7152 28.2862C18.7138 28.8573 18.9078 29.3375 19.297 29.7268C19.6863 30.1161 20.1658 30.3094 20.7356 30.3067ZM20.7356 24.2453C21.3081 24.2453 21.7883 24.0514 22.1762 23.6634C22.5641 23.2755 22.7574 22.796 22.7561 22.2249V16.1636C22.7561 15.5911 22.5621 15.1116 22.1742 14.725C21.7862 14.3384 21.3067 14.1445 20.7356 14.1431C20.1645 14.1418 19.685 14.3357 19.297 14.725C18.9091 15.1143 18.7152 15.5938 18.7152 16.1636V22.2249C18.7152 22.7973 18.9091 23.2775 19.297 23.6655C19.685 24.0534 20.1645 24.2467 20.7356 24.2453Z"
      fill="#FF766A"
    />
  </svg>
);
